import { Trans } from '@lingui/macro';
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/ui/card';
import { Button } from '@shared/ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
}

/**
 * Global error boundary component for catching and handling React errors
 * Provides user-friendly error messages and recovery options
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
    
    // TODO: Send error to monitoring service (e.g., Sentry)
    // errorReportingService.captureException(error, {
    //   extra: errorInfo,
    //   tags: { component: 'ErrorBoundary' }
    // });
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
      }

      // Default error UI
      return (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
              </div>
              <CardTitle><Trans id='shared.components.shared.ErrorBoundary.something-went-wrong'>Something went wrong</Trans></CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-muted-foreground"><Trans
                id='shared.components.shared.ErrorBoundary.we-encountered-an-unexpected-error'>We encountered an unexpected error. This might be a temporary issue.</Trans></p>
              
              {/* Show error details in development */}
              {import.meta.env.DEV && this.state.error && (
                <details className="text-left text-xs bg-muted p-3 rounded">
                  <summary className="cursor-pointer font-medium mb-2"><Trans id='shared.components.shared.ErrorBoundary.error-details'>Error Details</Trans></summary>
                  <pre className="whitespace-pre-wrap break-words">
                    {this.state.error.message}
                    {this.state.error.stack && `\n\n${this.state.error.stack}`}
                  </pre>
                </details>
              )}
              
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button onClick={this.resetError} className="flex items-center gap-2"><Trans id='shared.components.shared.ErrorBoundary.try-again'><RefreshCw className="h-4 w-4" />Try Again</Trans></Button>
                
                <Button 
                  variant="outline" 
                  onClick={() => window.location.href = '/'}
                  className="flex items-center gap-2"
                ><Trans id='shared.components.shared.ErrorBoundary.go-home'><Home className="h-4 w-4" />Go Home</Trans></Button>
              </div>
              
              <p className="text-xs text-muted-foreground"><Trans
                id='shared.components.shared.ErrorBoundary.if-this-problem-persists-please'>If this problem persists, please contact support.</Trans></p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook version of ErrorBoundary for functional components
 * Note: This is a simplified version - full error boundaries require class components
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error: Error) => {
    console.error('Error captured:', error);
    setError(error);
    
    // TODO: Send to error reporting service
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error; // This will be caught by the nearest ErrorBoundary
    }
  }, [error]);

  return { captureError, resetError };
};